import React, { useEffect, useMemo, useState } from "react";
import { Badge, Box, Divider, Group, Select, SimpleGrid, Skeleton, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { Alert, Button, ConfirmModal, spacePx } from "@xplatform/design-system";
import { IconTrash } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { EditorDrawer } from "@features/Doctype/FlowConfig/EditorDrawer";
import type { AccessUserDetail, OrganisationNode, Role, UserAccess, UserAccessUpdate } from "@models/access";
import { accessApi } from "@services/accessApi";
import { errorMessage } from "@hooks/monitoring/useApiResource";
import { useAccessUser, useEffectivePermissions, useEscalationGuard } from "@hooks/access";
import { LANGUAGES } from "./CreateUserModal";
import { organisationOptions } from "./organisations";

interface UserDrawerProps {
  userId: string | null;
  roots: OrganisationNode[];
  roles: Role[];
  canManage: boolean;
  onClose: () => void;
  /** A change was saved: the lists showing this user reload. */
  onChanged: () => void;
}

const STATUSES = ["ACTIVE", "INACTIVE"] as const;

/** BeeHive grants one role per organisation; `roles` is a list only because BeeHive's field is. */
const roleOf = (access: UserAccess): string | null => access.roles[0]?.id ?? null;

/**
 * The role to show: the application role, else the one BeeHive applies (an
 * organisation owner holds BeeHive's "Organisation Owner" and no application role).
 */
export const roleLabel = (access: UserAccess): string | null => access.roles[0]?.name ?? access.effectiveRole ?? null;

interface Profile {
  firstName: string;
  lastName: string;
  preferredLanguage: string;
  status: string;
}

const profileOf = (user: AccessUserDetail): Profile => ({
  firstName: user.firstName ?? "",
  lastName: user.lastName ?? "",
  preferredLanguage: user.preferredLanguage ?? "en",
  status: user.status ?? "ACTIVE",
});

/**
 * One user: their BeeHive profile, their access to this application per
 * organisation, and what they can effectively do. Their profile is global in
 * BeeHive (every application), so it is only editable when every organisation
 * of the user is in the scope; nobody edits their own user.
 */
const UserDrawer: React.FC<UserDrawerProps> = ({ userId, roots, roles, canManage, onClose, onChanged }) => {
  const { t } = useTranslation();
  const user = useAccessUser(userId);
  const { canGrantRole } = useEscalationGuard();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<UserAccess | null>(null);
  const [newOrg, setNewOrg] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<string | null>(null);
  const [effectiveOrg, setEffectiveOrg] = useState<string | null>(null);

  const detail = user.data && user.data.id === userId ? user.data : null;
  useEffect(() => {
    setProfile(detail ? profileOf(detail) : null);
    setEffectiveOrg(detail?.access.find((a) => a.isGranted)?.organisationId ?? null);
  }, [detail]);
  useEffect(() => {
    setError(null);
    setNotice(null);
    setNewOrg(null);
    setNewRole(null);
  }, [userId]);

  const effective = useEffectivePermissions(detail ? detail.id : null, effectiveOrg);

  const isSelf = detail?.isSelf === true;
  const canEditAccess = canManage && !isSelf && detail?.accessEditable === true;
  const canEditProfile = canEditAccess && detail?.profileEditable === true;
  const dirty = !!detail && !!profile && JSON.stringify(profile) !== JSON.stringify(profileOf(detail));

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: r.id, label: r.name, disabled: !canGrantRole(r) })),
    [roles, canGrantRole],
  );
  /** The options of one row: the user's current role stays shown even when the admin may not pick it. */
  const rowOptions = (access: UserAccess) =>
    access.roles.length === 0 || roleOptions.some((o) => o.value === access.roles[0].id)
      ? roleOptions
      : [...roleOptions, { value: access.roles[0].id, label: access.roles[0].name, disabled: true }];
  const granted = detail?.access.filter((a) => a.isGranted) ?? [];

  const changed = () => {
    setNotice(t("admin.access.users.drawer.takesEffect"));
    user.reload();
    effective.reload();
    onChanged();
  };

  /** Runs a change, then reloads the user (PUT /access answers with the access rows only). */
  const run = async (action: () => Promise<unknown>) => {
    setSaving(true);
    setError(null);
    try {
      await action();
      changed();
      return true;
    } catch (err) {
      setError(errorMessage(err));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveProfile = () => {
    if (!detail || !profile) return;
    void run(() => accessApi().updateProfile(detail.id, profile));
  };

  const setAccess = (access: Omit<UserAccessUpdate, "isGranted">, isGranted: boolean) =>
    detail ? run(() => accessApi().updateAccess(detail.id, { ...access, isGranted })) : Promise.resolve(false);

  // Core lists every permission of the licence with `granted`; only the granted ones are shown.
  const grantedModules = useMemo(
    () => (effective.data?.modules ?? [])
      .map((m) => ({ ...m, permissions: m.permissions.filter((p) => p.granted) }))
      .filter((m) => m.permissions.length > 0),
    [effective.data],
  );

  const name = detail ? [detail.firstName, detail.lastName].filter(Boolean).join(" ") || detail.email : "";

  return (
    <>
      <EditorDrawer
        opened={userId !== null}
        onClose={onClose}
        title={
          <Box>
            <Title order={4}>{name || t("admin.access.users.drawer.title")}</Title>
            {detail && <Text size="xs" c="dimmed">{detail.email}</Text>}
            {detail && granted.length > 0 && (
              <Group gap={spacePx[1]} mt={spacePx[1]}>
                {granted.map((access) => (
                  <Badge key={access.organisationId} variant="light" tt="none" size="sm">
                    {access.organisationName ?? access.organisationId}
                    {roleLabel(access) ? ` · ${roleLabel(access)}` : ""}
                  </Badge>
                ))}
              </Group>
            )}
          </Box>
        }
        dirty={dirty}
        saving={saving}
        error={error}
        onDismissError={() => setError(null)}
        saveLabel={t("admin.access.users.drawer.saveProfile")}
        onSave={saveProfile}
        canEdit={canEditProfile}
      >
        {!detail || !profile ? (
          user.error ? <Alert color="red">{user.error}</Alert> : <Skeleton h={240} />
        ) : (
          <Stack gap="lg">
            {isSelf && <Alert color="gray">{t("admin.access.users.drawer.self")}</Alert>}
            {!isSelf && !detail.profileEditable && (
              <Alert color="yellow">{t("admin.access.users.drawer.outsideScope")}</Alert>
            )}
            {notice && <Alert color="green" withCloseButton onClose={() => setNotice(null)}>{notice}</Alert>}

            <Stack gap="sm">
              <Text fw={600}>{t("admin.access.users.drawer.profile")}</Text>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <TextInput label={t("admin.access.users.create.firstName")} value={profile.firstName}
                           disabled={!canEditProfile}
                           onChange={(e) => setProfile({ ...profile, firstName: e.currentTarget.value })} />
                <TextInput label={t("admin.access.users.create.lastName")} value={profile.lastName}
                           disabled={!canEditProfile}
                           onChange={(e) => setProfile({ ...profile, lastName: e.currentTarget.value })} />
                <Select label={t("admin.access.users.create.language")} value={profile.preferredLanguage}
                        data={LANGUAGES.map((l) => ({ value: l, label: l.toUpperCase() }))}
                        disabled={!canEditProfile} allowDeselect={false}
                        onChange={(v) => setProfile({ ...profile, preferredLanguage: v ?? profile.preferredLanguage })}
                        comboboxProps={{ portalProps: { target: "#admin-portal-root" } }} />
                <Select label={t("admin.access.users.columns.status")} value={profile.status}
                        data={[...new Set([...STATUSES, profile.status])].map((s) => ({ value: s, label: s }))}
                        disabled={!canEditProfile} allowDeselect={false}
                        onChange={(v) => setProfile({ ...profile, status: v ?? profile.status })}
                        comboboxProps={{ portalProps: { target: "#admin-portal-root" } }} />
              </SimpleGrid>
            </Stack>

            <Divider />

            <Stack gap="sm">
              <Text fw={600}>{t("admin.access.users.drawer.access")}</Text>
              <Table verticalSpacing="xs" withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("admin.access.users.columns.organisation")}</Table.Th>
                    <Table.Th>{t("admin.access.users.columns.role")}</Table.Th>
                    <Table.Th w={64} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {granted.map((access) => (
                    <Table.Tr key={access.organisationId}>
                      <Table.Td>
                        <Text size="sm">{access.organisationName ?? access.organisationId}</Text>
                        {access.licenceName && (
                          <Text size="xs" c="dimmed">{t("admin.access.users.drawer.licence", { licence: access.licenceName })}</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={spacePx[1]}>
                          {/* A BeeHive role (organisation owner or admin) is not an application role: named, not selectable. */}
                          {access.roles.length === 0 && access.effectiveRole && (
                            <Group gap="xs">
                              <Text size="sm">{access.effectiveRole}</Text>
                              <Badge size="xs" variant="outline" color="gray">{t("admin.access.users.drawer.beehiveRole")}</Badge>
                            </Group>
                          )}
                          {(access.roles.length > 0 || canEditAccess) && (
                            <Select
                              size="xs"
                              w={240}
                              aria-label={t("admin.access.users.columns.role")}
                              data={rowOptions(access)}
                              value={roleOf(access)}
                              placeholder={t("admin.access.users.drawer.assignRole")}
                              disabled={!canEditAccess || !access.inScope || saving}
                              onChange={(roleId) => roleId && roleId !== roleOf(access) &&
                                void setAccess({ organisationId: access.organisationId, roleId }, true)}
                              comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
                            />
                          )}
                        </Stack>
                      </Table.Td>
                      <Table.Td>
                        {canEditAccess && access.inScope && (
                          <Button size="xs" variant="subtle" color="red" aria-label={t("admin.access.users.drawer.revoke")}
                                  onClick={() => setRevoking(access)}>
                            <IconTrash size={14} />
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                  {detail.access.every((a) => !a.isGranted) && (
                    <Table.Tr>
                      <Table.Td colSpan={3}><Text size="sm" c="dimmed">{t("admin.access.users.drawer.noAccess")}</Text></Table.Td>
                    </Table.Tr>
                  )}
                </Table.Tbody>
              </Table>
              {canEditAccess && (
                <Group align="flex-end" gap="sm" wrap="nowrap">
                  <Select
                    label={t("admin.access.users.columns.organisation")}
                    data={organisationOptions(roots, t("admin.access.organisations.inactive"))}
                    value={newOrg}
                    onChange={setNewOrg}
                    flex={1}
                    comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
                  />
                  <Select
                    label={t("admin.access.users.columns.role")}
                    data={roleOptions}
                    value={newRole}
                    onChange={setNewRole}
                    flex={1}
                    comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
                  />
                  <Button
                    disabled={!newOrg || !newRole || saving}
                    onClick={async () => {
                      if (await setAccess({ organisationId: newOrg!, roleId: newRole }, true)) {
                        setNewOrg(null);
                        setNewRole(null);
                      }
                    }}
                  >
                    {t("admin.access.users.drawer.grant")}
                  </Button>
                </Group>
              )}
            </Stack>

            <Divider />

            <Stack gap="sm">
              <Group justify="space-between">
                <Text fw={600}>{t("admin.access.users.drawer.effective")}</Text>
                <Select
                  size="xs"
                  aria-label={t("admin.access.users.columns.organisation")}
                  data={granted.map((a) => ({ value: a.organisationId, label: a.organisationName ?? a.organisationId }))}
                  value={effectiveOrg}
                  onChange={setEffectiveOrg}
                  w={240}
                  comboboxProps={{ portalProps: { target: "#admin-portal-root" } }}
                />
              </Group>
              {effective.error && <Alert color="red">{effective.error}</Alert>}
              {effective.loading && !effective.data && <Skeleton h={80} />}
              {grantedModules.map((module) => (
                <Stack key={module.name} gap={spacePx[1]}>
                  <Text size="xs" fw={600} c="dimmed" tt="uppercase">{module.name}</Text>
                  <Group gap={spacePx[1]}>
                    {module.permissions.map((p) => (
                      <Badge key={p.key} variant="light" color="gray" tt="none" title={p.name}>{p.key}</Badge>
                    ))}
                  </Group>
                </Stack>
              ))}
              {effective.data && grantedModules.length === 0 && (
                <Text size="sm" c="dimmed">{t("admin.access.users.drawer.noPermissions")}</Text>
              )}
            </Stack>
          </Stack>
        )}
      </EditorDrawer>

      <ConfirmModal
        opened={revoking !== null}
        onClose={() => setRevoking(null)}
        onConfirm={async () => {
          if (revoking) await setAccess({ organisationId: revoking.organisationId, roleId: null }, false);
          setRevoking(null);
        }}
        title={t("admin.access.users.drawer.revokeTitle")}
        message={t("admin.access.users.drawer.revokeMessage", {
          organisation: revoking?.organisationName ?? revoking?.organisationId ?? "",
        })}
        confirmLabel={t("admin.access.users.drawer.revoke")}
        cancelLabel={t("common.cancel")}
        loading={saving}
        danger
        portalProps={{ target: "#admin-portal-root" }}
      />
    </>
  );
};

export default UserDrawer;
